<script setup lang="ts">
import { computed } from 'vue'
import CollapsibleCard from './CollapsibleCard.vue'
import type { MomentumGateBreakdown } from '../../composables/useSignalPipeline'

const props = defineProps<{
  breakdown: MomentumGateBreakdown | null
  wz: number | null
  window: number | null
  stale: string | null
}>()
defineEmits<{ explain: [key: string] }>()

// The gate card's own pass/fail check is the raw wz-vs-threshold comparison
// (mirrors useSignalPipeline's momentumGatePassed) — independent of the VMG
// circuit breaker, which can additionally demote a wz-passing day to SELL.
// combinedThreshold already folds in the live wz_threshold when the
// breakdown is available; falls back to 0 (the raw gate default) when it
// isn't (e.g. cold-start where vix hasn't arrived yet).
const threshold = computed(() => props.breakdown?.combinedThreshold ?? 0)
const passed = computed(() => props.wz != null && props.wz > threshold.value)

const alert = computed(() => !!(props.breakdown?.gateBlocked || props.breakdown?.circuitTriggered))

const summary = computed(() => {
  const wzStr = props.wz != null ? (props.wz >= 0 ? '+' : '') + props.wz.toFixed(2) : '--'
  const thrStr = (threshold.value >= 0 ? '+' : '') + threshold.value.toFixed(2)
  const vmg = props.breakdown ? ` · VMG ${props.breakdown.vmgMode}` : ''
  return `${passed.value ? '✓ pass' : '✗ BLOCKED'} · wz ${wzStr} vs ${thrStr}${vmg}`
})
</script>

<template>
  <!-- Wrapper carries the legacy `vix-mom-gate-pill` testid whenever a live
       VMG breakdown exists, so it stays reachable from the top while the
       card body is collapsed. The testid lives ONLY here — a second copy
       on the inner breakdown block would trip Playwright strict-mode
       locators (2 matches). -->
  <div :data-testid="breakdown ? 'vix-mom-gate-pill' : undefined">
    <CollapsibleCard title="Momentum Gate" testid="imr-pill" :summary="summary" :alert="alert">
      <div class="gate-card" :class="passed ? 'gate-pass' : 'gate-fail'">
        <div class="gate-title">IMR Momentum ({{ window ?? 10 }}d) <span class="info-icon" @click.stop="$emit('explain', 'imr')">i</span></div>
        <div v-if="stale" class="stale-warning"
             title="latest:signal is frozen by the market-closed publish gate — values resume with the next session's ticks. This is by design, not a fault.">
          STALE — as of {{ stale }}
        </div>
        <div class="gate-value" :class="passed ? 'gv-pass' : 'gv-fail'">
          wz {{ wz != null ? (wz >= 0 ? '+' : '') + wz.toFixed(2) : '—' }}<span v-if="wz != null" :style="{ color: wz >= 0 ? '#3fb950' : '#f85149', marginLeft: '4px' }">{{ (wz >= 0 ? '↑' : '↓').repeat(Math.min(Math.max(Math.ceil(Math.abs(wz) / 0.15), 1), 5)) }}</span>
        </div>
        <div class="gate-status" :class="passed ? 'gv-pass' : 'gv-fail'">
          {{ passed ? '✓' : '✗' }} wz {{ (wz ?? 0) >= 0 ? '+' : '' }}{{ (wz ?? 0).toFixed(2) }} {{ passed ? '>' : '≤' }} {{ threshold.toFixed(2) }} threshold
        </div>
        <div v-if="threshold > 0" class="gate-vix-note">
          VIX {{ breakdown?.regimeLabel ?? 'normal' }} + {{ breakdown?.termLabel ?? 'neutral' }} raised bar from 0.00
        </div>
        <div v-else-if="threshold < 0" class="gate-vix-note">
          Dead-zone active: gate only fires SELL when wz ≤ {{ threshold.toFixed(2) }}
          (raw 0.00 widened by −{{ Math.abs(threshold).toFixed(2) }})
        </div>
      </div>

      <!-- Momentum Gate Breakdown — only shows components that the live
           VMG mode actually applies. Inactive components are dimmed +
           labeled "not enforced" so the operator never sees a "FAIL"
           against an unenforced threshold. -->
      <div v-if="breakdown" class="vmg-breakdown" :class="{ 'vmg-fail': breakdown.gateBlocked || breakdown.circuitTriggered }">
        <div class="vmg-header">
          Momentum Gate (VMG={{ breakdown.vmgMode }}) <span class="info-icon" @click.stop="$emit('explain', 'vmg')">i</span>
          <span v-if="breakdown.gateBlocked" class="vmg-blocked">— BLOCKED BUY</span>
          <span v-if="breakdown.circuitTriggered" class="vmg-blocked">— CIRCUIT BREAKER</span>
        </div>
        <div class="vmg-cols">
          <div class="vmg-col" :class="{ 'vmg-col-inactive': !breakdown.regimeActive }">
            <div class="vmg-label" style="color: #64b5f6;">
              VIX Regime
              <span v-if="!breakdown.regimeActive" class="vmg-inactive-tag">not enforced</span>
            </div>
            <div class="vmg-val"><span class="vmg-vix">VIX {{ breakdown.vix.toFixed(1) }}</span> <span class="vmg-dim">&rarr; {{ breakdown.regimeLabel }}</span></div>
            <div class="vmg-thr">wz &gt; <span class="vmg-thr-val">{{ breakdown.regimeThreshold.toFixed(2) }}</span></div>
          </div>
          <div class="vmg-col" :class="{ 'vmg-col-inactive': !breakdown.termActive }">
            <div class="vmg-label" style="color: #4db6ac;">
              Term Structure
              <span v-if="!breakdown.termActive" class="vmg-inactive-tag">not enforced</span>
            </div>
            <div class="vmg-val"><span :class="breakdown.contangoPct > 0 ? 'vmg-backwd' : 'vmg-contango'">{{ breakdown.contangoPct <= 0 ? 'Contango' : 'Backwd' }} {{ Math.abs(breakdown.contangoPct).toFixed(1) }}%</span> <span class="vmg-dim">&rarr; {{ breakdown.termLabel }}</span></div>
            <div class="vmg-thr">wz &gt; <span class="vmg-thr-val">{{ breakdown.termThreshold.toFixed(2) }}</span></div>
          </div>
          <div class="vmg-col" :class="{ 'vmg-col-inactive': !breakdown.circuitActive }">
            <div class="vmg-label" style="color: #9e9e9e;">
              Circuit Breaker
              <span v-if="!breakdown.circuitActive" class="vmg-inactive-tag">not enforced</span>
            </div>
            <div class="vmg-val" :class="breakdown.circuitTriggered ? 'vmg-circuit-fail' : 'vmg-circuit-ok'">VIX {{ breakdown.circuitTriggered ? '&gt;' : '&lt;' }} 35</div>
            <div :class="breakdown.circuitTriggered ? 'vmg-circuit-fail' : 'vmg-circuit-ok'">{{ breakdown.circuitTriggered ? 'TRIGGERED' : 'OK' }}</div>
          </div>
          <div class="vmg-col vmg-result">
            <div class="vmg-label" :style="{ color: breakdown.gatePasses ? '#4caf50' : '#f44336' }">Gate Result</div>
            <template v-if="breakdown.raisedBarActive">
              <div class="vmg-val"><span :class="breakdown.gatePasses ? 'vmg-pass' : 'vmg-fail-text'">wz {{ breakdown.wz >= 0 ? '+' : '' }}{{ breakdown.wz.toFixed(2) }}</span> <span class="vmg-dim">{{ breakdown.gatePasses ? '&gt;' : '&lt;' }}</span> <span class="vmg-thr-val">{{ breakdown.combinedThreshold.toFixed(2) }}</span></div>
              <div :class="breakdown.gatePasses ? 'vmg-pass' : 'vmg-fail-text'" style="font-weight: bold;">{{ breakdown.gatePasses ? 'PASS' : 'FAIL' }} <span class="vmg-dim" style="font-weight: normal;">({{ breakdown.headroom >= 0 ? '+' : '' }}{{ breakdown.headroom.toFixed(2) }})</span></div>
            </template>
            <template v-else>
              <div class="vmg-val vmg-dim">no threshold gate</div>
              <div :class="breakdown.circuitTriggered ? 'vmg-fail-text' : 'vmg-pass'" style="font-weight: bold;">
                {{ breakdown.circuitTriggered ? 'CIRCUIT FIRED' : (breakdown.vmgMode === 'circuit' ? 'CIRCUIT-only mode · pass' : 'gate disabled') }}
              </div>
            </template>
          </div>
        </div>
      </div>
    </CollapsibleCard>
  </div>
</template>

<style scoped>
.gate-card {
  border-radius: 4px;
  padding: 6px 8px;
  background: #0d1117;
  border-left: 3px solid #30363d;
}
.gate-pass { border-left-color: #3fb950; }
.gate-fail { border-left-color: #f85149; }

.gate-title { font-size: 0.7rem; color: #6e7681; text-transform: uppercase; }
.gate-value { font-size: 1.325rem; font-weight: 700; font-family: 'JetBrains Mono', monospace; }
.stale-warning { font-size: 0.65rem; color: #f85149; font-weight: 700; background: rgba(248,81,73,0.1); padding: 1px 6px; border-radius: 3px; margin-top: 2px; }
.gv-pass { color: #3fb950; }
.gv-fail { color: #f85149; }
.gate-status { font-size: 0.78rem; }
.gate-vix-note { font-size: 0.65rem; color: #d29922; margin-top: 2px; font-style: italic; }

.info-icon {
  display: inline-flex; align-items: center; justify-content: center;
  width: 14px; height: 14px; border-radius: 50%;
  background: #21262d; color: #8b949e; font-size: 0.55rem;
  cursor: pointer; font-style: italic; font-weight: 600;
  margin-left: 4px; vertical-align: middle;
}
.info-icon:hover { background: #30363d; color: #c9d1d9; }

.vmg-breakdown { background: #141414; border: 1px solid #2a2a2a; border-radius: 6px; padding: 10px 14px; margin-top: 8px; }
.vmg-breakdown.vmg-fail { border-color: #f44336; }
.vmg-header { font-size: 0.7rem; color: #6e7681; text-transform: uppercase; letter-spacing: 1px; font-weight: 700; margin-bottom: 8px; border-bottom: 1px solid #222; padding-bottom: 4px; font-family: 'JetBrains Mono', monospace; }
.vmg-blocked { color: #f44336; }
.vmg-cols { display: flex; gap: 16px; flex-wrap: wrap; font-family: 'JetBrains Mono', monospace; font-size: 11px; }
.vmg-col { min-width: 120px; }
.vmg-col-inactive { opacity: 0.45; }
.vmg-result { border-left: 2px solid #333; padding-left: 14px; }
.vmg-label { font-size: 9px; text-transform: uppercase; margin-bottom: 2px; }
.vmg-inactive-tag {
  display: inline-block; margin-left: 6px;
  font-size: 8px; font-weight: 400; color: #666;
  background: #21262d; padding: 1px 5px; border-radius: 2px;
  text-transform: none; letter-spacing: 0;
}
.vmg-val { color: #ccc; }
.vmg-dim { color: #555; font-size: 10px; }
.vmg-thr { color: #e0e0e0; }
.vmg-thr-val { color: #ff9800; font-weight: 700; }
.vmg-vix { color: #ff9800; }
.vmg-backwd { color: #f44336; }
.vmg-contango { color: #4caf50; }
.vmg-circuit-ok { color: #4caf50; }
.vmg-circuit-fail { color: #f44336; font-weight: 700; }
.vmg-pass { color: #4caf50; font-weight: 700; }
.vmg-fail-text { color: #f44336; font-weight: 700; }

@media (max-width: 768px) {
  .vmg-cols { gap: 8px; }
  .vmg-col { min-width: 80px; font-size: 10px; }
  .vmg-result { border-left: none; padding-left: 0; border-top: 1px solid #333; padding-top: 6px; width: 100%; }
}
</style>
